/**
 * adminStyles.js — shared AdminScreen StyleSheet (god-file decomposition).
 * Verbatim move from AdminScreen.jsx; used by the shell and extracted tabs.
 */

import { StyleSheet, Platform } from 'react-native';
import { uiVar } from './floodlight/uiStyleCss';
import { ADM_DISPLAY } from './floodlight/vars/admin';

// Floodlight (v1.70): look-dependent values are `uiVar(<adm name>, <classic>)`;
// Classic never defines the variables, so the fallback is what renders.

export const adminStyles = StyleSheet.create({
  container: { flex: 1, backgroundColor: uiVar('adm-paper', '#0b0b0b') },
  
  // Main layout with sidebar
  mainLayout: {
    flex: 1,
    flexDirection: 'row',
  },
  
  // Sidebar styles
  sidebar: {
    width: 240,
    backgroundColor: uiVar('adm-sidebar', '#111'),
    borderRightWidth: uiVar('adm-rule-w', '1px'),
    borderRightColor: uiVar('adm-rule', 'rgba(255,255,255,0.08)'),
  },
  sidebarCollapsed: {
    width: 64,
  },
  sidebarMobile: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 100,
    width: 280,
  },
  sidebarHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: uiVar('adm-rule-w', '1px'),
    borderBottomColor: uiVar('adm-rule', 'rgba(255,255,255,0.08)'),
  },
  sidebarHeaderCollapsed: {
    justifyContent: 'center',
    paddingHorizontal: 12,
  },
  sidebarTitle: {
    color: uiVar('adm-ink', '#fff'),
    fontSize: 18,
    fontWeight: '700',
    ...ADM_DISPLAY,
    letterSpacing: uiVar('adm-display-track', 'normal'),
  },
  closeSidebarBtn: {
    padding: 4,
  },
  collapseBtn: {
    padding: 6,
    borderRadius: uiVar('adm-radius-sm', '6px'),
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  sidebarNav: {
    flex: 1,
  },
  navGroup: {
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  navGroupBorder: {
    borderTopWidth: 1,
    borderTopColor: uiVar('adm-hair', 'rgba(255,255,255,0.06)'),
  },
  navGroupTitle: {
    color: uiVar('adm-faint', 'rgba(255,255,255,0.4)'),
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: uiVar('adm-label-track', '0.5px'),
    marginBottom: 8,
    paddingHorizontal: 8,
  },
  navGroupLabs: {
    marginHorizontal: 8,
    marginVertical: 4,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: uiVar('adm-radius-md', '10px'),
    backgroundColor: uiVar('adm-labs-bg', 'rgba(162, 89, 255, 0.08)'),
    borderWidth: 1,
    borderColor: uiVar('adm-labs-border', 'rgba(162, 89, 255, 0.22)'),
  },
  navGroupTitleLabs: {
    color: uiVar('adm-labs-title', '#c4b5fd'),
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: uiVar('adm-radius-sm', '8px'),
    marginBottom: 2,
    borderLeftWidth: uiVar('adm-nav-bar-w', '0px'),
    borderLeftColor: 'transparent',
  },
  navItemCollapsed: {
    justifyContent: 'center',
    paddingHorizontal: 0,
  },
  navItemActive: {
    backgroundColor: uiVar('adm-nav-active-bg', 'rgba(167, 139, 250, 0.15)'),
    borderLeftColor: uiVar('adm-nav-active-bar', 'transparent'),
  },
  navItemText: {
    color: uiVar('adm-ink2', 'rgba(255,255,255,0.7)'),
    fontSize: 14,
    fontWeight: '500',
  },
  navItemTextActive: {
    color: uiVar('adm-nav-active-ink', '#a78bfa'),
    fontWeight: '600',
  },
  sidebarFooter: {
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: uiVar('adm-hair', 'rgba(255,255,255,0.06)'),
    marginTop: 12,
  },
  sidebarFooterCollapsed: {
    padding: 8,
  },
  backHomeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: uiVar('adm-radius-sm', '8px'),
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  backHomeBtnCollapsed: {
    justifyContent: 'center',
    paddingHorizontal: 0,
  },
  backHomeBtnText: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 14,
    fontWeight: '500',
  },
  
  // Content area
  contentArea: {
    flex: 1,
    backgroundColor: uiVar('adm-paper', '#0b0b0b'),
  },
  mobileTopBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderBottomWidth: uiVar('adm-rule-w', '1px'),
    borderBottomColor: uiVar('adm-rule', 'rgba(255,255,255,0.08)'),
  },
  menuBtn: {
    padding: 8,
  },
  mobileTitle: {
    color: uiVar('adm-ink', '#fff'),
    fontSize: 16,
    fontWeight: '600',
    ...ADM_DISPLAY,
    letterSpacing: uiVar('adm-display-track', 'normal'),
  },
  body: { flex: 1, padding: 16 },
  
  // Sidebar overlay for mobile
  sidebarOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: uiVar('adm-overlay', 'rgba(0,0,0,0.6)'),
    zIndex: 99,
  },
  tabWrap: { flex: 1 },
  sectionTitle: { color: uiVar('adm-ink', '#fff'), fontSize: 18, fontWeight: '700', marginBottom: 12, ...ADM_DISPLAY, letterSpacing: uiVar('adm-display-track', 'normal') },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 },
  label: { color: 'rgba(255,255,255,0.9)', width: 100 },
  input: { flex: 1, height: 40, paddingHorizontal: 10, borderRadius: uiVar('adm-radius-sm', '8px'), borderWidth: 1, borderColor: uiVar('adm-input-border', 'rgba(255,255,255,0.18)'), color: uiVar('adm-ink', '#fff') },
  btn: { paddingVertical: 10, paddingHorizontal: 12, borderRadius: uiVar('adm-radius-sm', '8px'), backgroundColor: uiVar('adm-btn-bg', '#ffffff'), alignItems: 'center' },
  btnText: { color: uiVar('adm-btn-ink', '#000'), fontWeight: '700', textTransform: uiVar('adm-display-case', 'none'), letterSpacing: uiVar('adm-label-track', 'normal') },
  outputBox: { flex: 1, backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: 12, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)' },
  outputText: { color: 'rgba(255,255,255,0.85)', fontFamily: Platform.OS === 'web' ? 'monospace' : undefined, whiteSpace: Platform.OS === 'web' ? 'pre-wrap' : undefined },
  userRow: { flexDirection: 'column', alignItems: 'stretch', gap: 10, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)', marginBottom: 8 },
  userRowMobile: { flexDirection: 'column', alignItems: 'stretch', paddingVertical: 12 },
  userEmail: { color: uiVar('adm-ink', '#fff'), fontWeight: '600' },
  userMeta: { color: 'rgba(255,255,255,0.7)', fontSize: 13 },
  userActions: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', maxWidth: '100%' },
  actionGroupLabel: { color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: '600', marginBottom: 6, textTransform: 'uppercase' },
  smallBtn: { paddingVertical: 8, paddingHorizontal: 10, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.14)' },
  smallBtnMobile: { paddingVertical: 6, paddingHorizontal: 8 },
  smallBtnText: { color: uiVar('adm-ink', '#fff'), fontWeight: '600', fontSize: 12 },
  
  // Mobile Dropdown Styles
  mobileDropdownGroup: {
    marginBottom: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    overflow: 'hidden',
  },
  mobileDropdownHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    backgroundColor: 'rgba(255,255,255,0.06)',
  },
  mobileDropdownHeaderText: {
    color: uiVar('adm-ink', '#fff'),
    fontSize: 13,
    fontWeight: '600',
  },
  mobileDropdownArrow: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 12,
  },
  mobileDropdownContent: {
    backgroundColor: 'rgba(255,255,255,0.02)',
  },
  mobileDropdownItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.08)',
  },
  mobileDropdownItemText: {
    color: uiVar('adm-ink', '#fff'),
    fontSize: 13,
    fontWeight: '500',
  },
  hintText: { color: 'rgba(255,255,255,0.8)' },
  caption: { color: 'rgba(255,255,255,0.7)', marginTop: 8 },
  groupHeader: { color: 'rgba(255,255,255,0.9)', fontWeight: '700', marginTop: 12, marginBottom: 6 },
  card: { padding: 12, borderRadius: uiVar('adm-radius-lg', '10px'), borderWidth: 1, borderColor: uiVar('adm-hair', 'rgba(255,255,255,0.12)'), backgroundColor: uiVar('adm-card', 'rgba(255,255,255,0.03)'), marginBottom: 12 },
  tag: { paddingVertical: 6, paddingHorizontal: 10, borderRadius: 14, borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)' },
  tagActive: { backgroundColor: 'rgba(255,255,255,0.18)', borderColor: 'rgba(255,255,255,0.28)' },
  tagText: { color: 'rgba(255,255,255,0.85)', fontWeight: '600' },
  tagTextActive: { color: '#000' },
  link: { color: '#9fd3ff', textDecorationLine: 'underline' },
  betRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)' },
  betMain: { color: uiVar('adm-ink', '#fff'), fontWeight: '700' },
  betMeta: { color: 'rgba(255,255,255,0.7)' }
});
